import React, { useState } from 'react';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { alpha, useTheme } from '@mui/material/styles';
import { FileCheck2, FileLock2 } from 'lucide-react';
import { Link, useLocation } from 'react-router';
import { TenantMenuItemsExternalInner, UserReadExternal } from '@askporter/client-grieg-lyric';
import { useConfig } from '@askporter/config-provider';
import { IconFolder } from '@askporter/utils';
import { Card } from '../../../../../Card';
import { Icon, IconSize } from '../../../../../Icon';
import { Typography } from '../../../../../Typography';
import { UserAvatar } from '../../../../../UserAvatar';
import { disabledNavStyle, IconLink, NavbarLink } from '../../../../utils';
import { getSecondaryItems } from '../../utils';
import MenuListItems from '../MenuListItems';
interface ProfileCardAndMenuProps {
  userProfile: Partial<UserReadExternal>;
  t: (key: string) => string;
  tenantNavButtons: TenantMenuItemsExternalInner[];
  mainItems: Array<IconLink | NavbarLink>;
  handleSignOut: () => void;
}
const ProfileCardAndMenu: React.FC<ProfileCardAndMenuProps> = ({
  userProfile,
  t,
  tenantNavButtons,
  mainItems,
  handleSignOut,
}) => {
  const config = useConfig();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const secondaryItems = getSecondaryItems(mainItems);
  const location = useLocation();
  const { isDisabled, disabledNavStyles } = disabledNavStyle(location.pathname, '/app/account');
  const theme = useTheme();
  return (
    <Box
      className="profile-card-and-menu"
      sx={{
        position: 'fixed',
        bottom: '0px',
        width: '340px',
        p: '16px',
        '&.profile-card-and-menu': { '& .MuiCard-root': { px: 0, pb: 0, pt: '6px' } },
        boxSizing: 'border-box',
      }}
    >
      <Collapse in={isMenuOpen} timeout={300} easing="ease-in-out">
        <Card
          readOnly={true}
          sx={{
            mb: '8px',
            '&.MuiCard-root': {
              borderRadius: '12px !important',
            },
          }}
        >
          <ListItem
            disablePadding
            sx={{
              px: '8px',
              '&:hover': { px: '8px', borderRadius: '6px' },
              pb: '5px',
            }}
          >
            <ListItemButton
              to="/app/account"
              state={{ from: location.pathname }}
              component={Link}
              disabled={isDisabled}
              data-testid="my-account-btn"
              sx={{
                ...disabledNavStyles,
                fontWeight: '600',
                backgroundColor: isDisabled ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                px: '12px',
                borderRadius: '6px',
                '&:hover': {
                  px: '12px',
                  backgroundColor: '#F9FAFB',
                },
              }}
            >
              <ListItemIcon
                sx={{ minWidth: '32px', color: isDisabled ? alpha(theme.palette.primary.main, 0.6) : 'inherit' }}
              >
                <Icon
                  size={IconSize.MD}
                  folder={'design-system/user'}
                  isStrokeColor={isDisabled}
                  fillColor={isDisabled ? alpha(theme.palette.primary.main, 0.6) : 'inherit'}
                />
              </ListItemIcon>
              <ListItemText
                sx={{
                  '& span': {
                    fontWeight: '600',
                    color: isDisabled ? alpha(theme.palette.primary.main, 0.8) : '#414651',
                  },
                }}
              >
                {t('global:header:nav:my_account')}
              </ListItemText>
            </ListItemButton>
          </ListItem>
          <MenuListItems navItems={secondaryItems} t={t} divider={true} withGap={true} />
          {tenantNavButtons?.map((item, index) => (
            <ListItem
              disablePadding
              key={index}
              sx={{
                px: '8px',
                '&:hover': { px: '8px', borderRadius: '6px' },
                pb: index === tenantNavButtons.length - 1 ? '6px' : '5px',
              }}
            >
              <ListItemButton
                component={'a'}
                href={item?.linkAddress}
                target={item?.openInNew ? '_blank' : undefined}
                sx={{
                  width: '200px',
                  textDecoration: 'none',
                  color: 'inherit',
                  px: '12px',
                  borderRadius: '6px',
                  '&:hover': {
                    px: '12px',
                    backgroundColor: '#F9FAFB',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: '32px' }}>
                  <Icon size={IconSize.MD} folder={item?.iconPath as IconFolder} />
                </ListItemIcon>
                <ListItemText sx={{ '& span': { fontWeight: '600', color: '#414651' } }}>
                  {item?.linkTitle}
                </ListItemText>
              </ListItemButton>
            </ListItem>
          ))}
          {tenantNavButtons?.length > 0 && <Divider />}
          {/* Wraps a Card that contains its own ListItems — rendered as a div, not an <li>, so those
              ListItems don't end up nested inside this one (invalid HTML: <li> inside <li>). */}
          <ListItem component="div" sx={{ display: 'block', p: 0, backgroundColor: '#F5F5F5' }}>
            <Card
              sx={{
                borderTopLeftRadius: '0 !important',
                borderTopRightRadius: '0 !important',
                border: 0,
                borderBottom: '1px solid #E9EAEB',
                '&.MuiCard-root': {
                  borderBottomLeftRadius: '12px !important',
                  borderBottomRightRadius: '12px !important',
                },
              }}
              readOnly={true}
            >
              <ListItem
                sx={{
                  pt: 0,
                  px: '8px',
                  '&:hover': { px: '8px', borderRadius: '6px' },
                  pb: '2px',
                }}
              >
                <ListItemButton
                  aria-label={t('ns.global:header:nav:terms')}
                  data-testid="terms"
                  onClick={() => window.open(config.tenantDisplay?.links?.termsOfUseUrl, '_blank')}
                  sx={{
                    width: '200px',
                    textDecoration: 'none',
                    color: 'inherit',
                    px: '12px',
                    borderRadius: '6px',
                    '&:hover': {
                      px: '12px',
                      backgroundColor: '#F9FAFB',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: '32px' }}>
                    <FileCheck2 color="#A4A7AE" size={20} />
                  </ListItemIcon>
                  <ListItemText sx={{ '& span': { fontWeight: '600', color: '#414651' } }}>
                    {t('global:header:nav:terms')}
                  </ListItemText>
                </ListItemButton>
              </ListItem>
              <ListItem
                sx={{
                  pt: 0,
                  px: '8px',
                  '&:hover': { px: '8px', borderRadius: '6px' },
                }}
              >
                <ListItemButton
                  aria-label={t('ns.global:header:nav:policy')}
                  data-testid="policy"
                  onClick={() => window.open(config.tenantDisplay?.links?.privacyPolicyUrl, '_blank')}
                  sx={{
                    width: '200px',
                    textDecoration: 'none',
                    color: 'inherit',
                    px: '12px',
                    borderRadius: '6px',
                    '&:hover': {
                      px: '12px',
                      backgroundColor: '#F9FAFB',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: '32px' }}>
                    <FileLock2 color="#A4A7AE" size={20} />
                  </ListItemIcon>
                  <ListItemText sx={{ '& span': { fontWeight: '600', color: '#414651' } }}>
                    {t('global:header:nav:policy')}
                  </ListItemText>
                </ListItemButton>
              </ListItem>
            </Card>
          </ListItem>
          <ListItem sx={{ backgroundColor: '#F5F5F5', p: 0 }}>
            <ListItemButton aria-label={t('common:sign_out:button')} data-testid="sign-out" onClick={handleSignOut}>
              <ListItemIcon sx={{ minWidth: '32px' }}>
                <Icon size={IconSize.MD} folder="design-system/logout" />
              </ListItemIcon>
              <ListItemText sx={{ '& span': { fontWeight: '600', color: '#414651' } }}>
                {t('global:header:nav:logout')}
              </ListItemText>
            </ListItemButton>
          </ListItem>
        </Card>
      </Collapse>
      <Card
        sx={{
          '&.MuiCard-root': { width: '100%', borderRadius: '12px !important', pt: '0 !important' },
          '& .MuiCardActionArea-root': { p: '12px', position: 'relative' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }} onClick={() => setIsMenuOpen(!isMenuOpen)}>
          <UserAvatar
            givenName={userProfile.givenName}
            familyName={userProfile.familyName}
            imagePath={userProfile.profilePhoto?.filePath}
          />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '70%',
              whiteSpace: 'nowrap',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: '600',
                lineHeight: '20px',
                color: '#181D27',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {userProfile.givenName} {userProfile.familyName}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                lineHeight: '20px',
                color: '#535862',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                pointerEvents: 'none',
              }}
            >
              {userProfile.email}
            </Typography>
          </Box>
          <UnfoldMoreIcon sx={{ position: 'absolute', right: '6px', top: '12px' }} />
        </Box>
      </Card>
    </Box>
  );
};
export default ProfileCardAndMenu;
